import { formaterJour, formaterMontant, formaterPeriode, jourAParis } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, ErreurApi, lire, requetes } from '../api'
import { formaterInstant } from '../composants/Historique'
import { Icone } from '../composants/Icone'
import { Logo } from '../composants/Logo'
import { Vide } from '../composants/Section'
import { EntetePage } from '../composants/Ui'

// Collecteurs à la demande (décision de Pierre-Marie du 21/10/2026) : plus aucune collecte ne
// part seule. Chaque connexion visible, celles dont le lancement est nécessaire en tête avec
// leurs raisons (tâche qui l'attend, échec, passage du mois, documents manquants, fréquence
// écoulée), puis celles qui tournent, celles à jour, et celles qu'on ne lance pas d'ici. Les
// raisons sont décidées dans le cœur (packages/core/src/collecteurs.ts), le lancement par le
// serveur (POST /api/portails/:id/collecter, comme dans Réglages, Portails).

type Collecteur = InferResponseType<typeof api.portails.collecteurs.$get, 200>[number]

const STATUTS: Record<string, string> = {
  en_file: 'en file',
  en_cours: 'en cours',
  attente_humain: 'attend une vérification',
  reprise: 'reprend',
  terminee: 'terminée',
  echec: 'en échec',
  bloquee: 'bloquée',
  expiree: 'expirée',
  annulee: 'annulée',
}

/** À lancer : une raison au moins. En cours : une collecte tourne. Les autres : à jour ou hors d'ici. */
export function groupesCollecteurs<
  C extends { raisons: unknown[]; collecte: unknown; active: boolean; robot: boolean },
>(collecteurs: readonly C[]) {
  return {
    aLancer: collecteurs.filter((c) => c.raisons.length > 0),
    enCours: collecteurs.filter((c) => c.raisons.length === 0 && c.collecte !== null),
    aJour: collecteurs.filter(
      (c) => c.raisons.length === 0 && c.collecte === null && c.active && c.robot,
    ),
    horsDIci: collecteurs.filter(
      (c) => c.raisons.length === 0 && c.collecte === null && (!c.active || !c.robot),
    ),
  }
}

/** La dernière collecte telle que la liste la montre : sa naissance et son statut. */
type EtatCollecte = {
  collecte: unknown
  derniere: { creeLe: string; statut: string } | null
}
const etatDerniere = (c: EtatCollecte) =>
  c.derniere ? `${c.derniere.creeLe}|${c.derniere.statut}` : ''

/**
 * Message d'un lancement. « Lancé » ne vaut que jusqu'à la fin de la collecte qu'il annonce :
 * plus rien ne tourne et la dernière collecte a changé depuis le clic, la ligne « Dernière
 * collecte » dit l'issue. Une erreur reste.
 */
export type MessageLancement = { texte: string; erreur: boolean; avant: string }

export function messageEnCours(m: MessageLancement | null, c: EtatCollecte): boolean {
  if (!m) return false
  return m.erreur || c.collecte !== null || etatDerniere(c) === m.avant
}

/** Ce que dit le serveur une fois le lancement accepté. */
function messageLance(c: Collecteur): string {
  if (c.releve === 'ordinateur')
    return `${c.libelle} : lancé. « ${c.ordinateur ?? 'L’ordinateur'} » le prend à son prochain tour, s’il est allumé et Chrome ouvert.`
  return `${c.libelle} : lancé.`
}

export function Collecteurs() {
  const client = useQueryClient()
  const liste = useQuery({
    ...requetes.collecteurs,
    // Une collecte tourne : la liste suit ses étapes.
    refetchInterval: (q) => (q.state.data?.some((c) => c.collecte !== null) ? 5_000 : 60_000),
  })
  const [occupes, setOccupes] = useState<ReadonlySet<string>>(new Set())
  const [messages, setMessages] = useState<Record<string, MessageLancement>>({})

  async function lancer(collecteurs: readonly Collecteur[]) {
    setOccupes(new Set(collecteurs.map((c) => c.id)))
    for (const c of collecteurs) {
      const avant = etatDerniere(c)
      try {
        await lire(api.portails[':id'].collecter.$post({ param: { id: c.id } }))
        setMessages((m) => ({ ...m, [c.id]: { texte: messageLance(c), erreur: false, avant } }))
      } catch (e) {
        const texte = e instanceof ErreurApi ? e.message : 'Lancement impossible.'
        setMessages((m) => ({ ...m, [c.id]: { texte, erreur: true, avant } }))
      }
    }
    // La liste, Réglages, Portails et les bilans en dépendent.
    await client.invalidateQueries({ queryKey: ['portails'] })
    setOccupes(new Set())
  }

  const g = groupesCollecteurs(liste.data ?? [])
  const lancables = g.aLancer.filter((c) => c.lancable)
  // Une phrase par ordinateur muet : ses collectes attendent sans qu'il en prenne aucune.
  const muets = [...new Set((liste.data ?? []).map((c) => c.ordinateurMuet).filter((t) => t))]
  const ligne = (c: Collecteur, enAvant: boolean) => (
    <LigneCollecteur
      key={c.id}
      c={c}
      enAvant={enAvant}
      occupe={occupes.has(c.id)}
      message={messageEnCours(messages[c.id] ?? null, c) ? messages[c.id]! : null}
      onLancer={() => void lancer([c])}
    />
  )

  return (
    <main>
      <EntetePage surtitre="Comptabilité" titre="Collecteurs" />
      <section className="carte section">
        <div className="section-titre">
          <h2>À lancer{g.aLancer.length > 0 ? ` (${g.aLancer.length})` : ''}</h2>
          {lancables.length > 1 && (
            <button
              type="button"
              className="bouton bouton-principal"
              disabled={occupes.size > 0}
              onClick={() => void lancer(lancables)}
            >
              <Icone nom="play_arrow" />
              Tout lancer ({lancables.length})
            </button>
          )}
        </div>
        <p className="discret">
          Rien ne part seul : chaque collecteur se lance ici, ou depuis la tâche qui l’attend. Ceux
          de l’ordinateur ouvrent un onglet dans votre Chrome, où vous restez connecté ; si le
          portail demande un code, l’appli vous le demande aussitôt.
        </p>
        {muets.map((texte) => (
          <p key={texte} className="alerte">
            {texte} S’il est allumé et Chrome ouvert, relancer son collecteur : dans le Terminal,{' '}
            <code>launchctl kickstart -k gui/$(id -u)/fr.admin-pmvb.collecteur</code>
          </p>
        ))}
        {liste.isPending && <Vide>Chargement…</Vide>}
        {liste.data && g.aLancer.length === 0 && <Vide>Rien à lancer : tout est à jour.</Vide>}
        {g.aLancer.map((c) => ligne(c, true))}
      </section>
      {g.enCours.length > 0 && (
        <section className="carte section">
          <div className="section-titre">
            <h2>En cours ({g.enCours.length})</h2>
          </div>
          {g.enCours.map((c) => ligne(c, false))}
        </section>
      )}
      {g.aJour.length > 0 && (
        <section className="carte section">
          <div className="section-titre">
            <h2>À jour ({g.aJour.length})</h2>
          </div>
          {g.aJour.map((c) => ligne(c, false))}
        </section>
      )}
      {g.horsDIci.length > 0 && (
        <section className="carte section">
          <div className="section-titre">
            <h2>Désactivés ou sans collecte automatique ({g.horsDIci.length})</h2>
          </div>
          {g.horsDIci.map((c) => ligne(c, false))}
        </section>
      )}
      <p className="discret">
        Ajouter ou régler une connexion :{' '}
        <Link to="/reglages" search={{ onglet: 'connexions', connexion: 'portails' }}>
          Réglages, Connexions, Portails
        </Link>
        .
      </p>
    </main>
  )
}

function LigneCollecteur({
  c,
  enAvant,
  occupe,
  message,
  onLancer,
}: {
  c: Collecteur
  enAvant: boolean
  occupe: boolean
  message: MessageLancement | null
  onLancer: () => void
}) {
  const d = c.derniere
  const ouverte = c.collecte !== null
  // Validé pour une autre version de la règle : c'est le bilan qui débloque, pas « Lancer ».
  const revalider = c.raisons.some((r) => r.type === 'revalider')
  return (
    <div className="ligne">
      <div className="ligne-contenu">
        <div className="ligne-titre">
          <Logo cle={c.fournisseur} nom={c.libelle} taille={24} />
          <strong>{c.libelle}</strong>
          {!c.active && <span className="badge">Désactivé</span>}
          {c.active && !c.robot && <span className="badge">Dépôt à la main</span>}
          {ouverte && (
            <span className={`badge${c.defi ? ' ton-ambre' : ''}`}>
              {c.justificatifs ? 'Passage' : 'Collecte'} {STATUTS[c.collecte!.statut]}
            </span>
          )}
        </div>
        <div className="discret">
          {c.entite ?? 'Sans entité'} · {c.fournisseurLibelle} ·{' '}
          {c.releve === 'serveur'
            ? 'relevé par le serveur'
            : c.releve === 'ordinateur'
              ? `relevé sur ${c.ordinateur ?? 'un ordinateur'}`
              : 'relevé par aucun ordinateur'}{' '}
          · titulaire {c.titulaire}
        </div>
        {c.raisons.length > 0 && (
          <ul className="raisons-collecteur">
            {c.raisons.map((r) => (
              <li key={r.type}>{r.texte}</li>
            ))}
          </ul>
        )}
        {c.moisAVenir.length > 0 && !c.raisons.some((r) => r.type === 'a_venir') && (
          <div className="discret">
            Facture à venir : {c.moisAVenir.map(formaterPeriode).join(', ')}
          </div>
        )}
        {c.aJustifier > 0 && (
          <div className="discret">
            {c.aJustifier} opération{c.aJustifier > 1 ? 's' : ''} à justifier : voir le bilan.
          </div>
        )}
        {c.commandesIncompletes && (
          <div className="discret">
            {c.commandesIncompletes.nombre === 1
              ? 'Une commande sans toutes ses factures'
              : `${c.commandesIncompletes.nombre} commandes sans toutes leurs factures`}{' '}
            ({formaterMontant(c.commandesIncompletes.manqueCts)} sans facture) : les demander sur le
            site, puis lancer.
          </div>
        )}
        {c.taches.map((t) => (
          <div key={t.id} className="discret">
            Tâche :{' '}
            <Link to="/taches/$id" params={{ id: t.id }}>
              {t.titre}
            </Link>
          </div>
        ))}
        <div className="discret">
          {d
            ? `${c.justificatifs ? 'Dernier passage' : 'Dernière collecte'} le ${formaterInstant(d.creeLe)} : ${STATUTS[d.statut] ?? d.statut}${
                d.statut === 'terminee' && !c.justificatifs
                  ? ` (${d.documentsNouveaux} nouveau${d.documentsNouveaux > 1 ? 'x' : ''})`
                  : ''
              }`
            : 'Jamais lancé'}
          {!c.justificatifs &&
            c.active &&
            c.prochaineCollecteLe &&
            !ouverte &&
            c.raisons.length === 0 &&
            ` · à relancer à partir du ${formaterJour(jourAParis(new Date(c.prochaineCollecteLe)))}`}
        </div>
        {d?.erreur && !ouverte && <div className="discret">{d.erreur}</div>}
        {c.defi && (
          <p className="alerte">
            Le portail demande : {c.defi.libelle}.{' '}
            {c.defi.enDirect ? (
              <Link to="/defi/$id" params={{ id: c.defi.id }}>
                Répondre
              </Link>
            ) : (
              '« Lancer » le relance ; le titulaire répond dans l’appli.'
            )}
          </p>
        )}
        {c.active && c.sansReleve && <p className="alerte">{c.sansReleve}</p>}
        {message && (
          <p className={message.erreur ? 'alerte' : 'discret'} role="status">
            {message.texte}
          </p>
        )}
      </div>
      <div className="ligne-actions">
        {c.justificatifs && (
          <Link
            to="/justificatifs/$id"
            params={{ id: c.id }}
            className={revalider ? 'bouton bouton-principal' : 'bouton-discret'}
          >
            Bilan
          </Link>
        )}
        {c.lancable && (
          <button
            type="button"
            className={enAvant && !revalider ? 'bouton bouton-principal' : 'bouton-discret'}
            disabled={occupe}
            onClick={onLancer}
          >
            {enAvant && <Icone nom="play_arrow" />}
            {occupe ? 'Lancement…' : c.justificatifs ? 'Lancer le passage' : 'Lancer'}
          </button>
        )}
      </div>
    </div>
  )
}
